import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EmailIcon from '@mui/icons-material/Email';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../context/AuthContext';

interface InstanceStatus {
  is_healthy: boolean;
  is_enabled: boolean;
  server: string | null;
  path: string | null;
  last_check: string | null;
  alert_count_24h: number;
  alerts_by_severity: {
    CRITICAL: number;
    HIGH: number;
    MEDIUM: number;
    LOW: number;
  };
  cpu_load: number | null;
  memory_percent: number | null;
  memory_used_mb: number | null;
  memory_total_mb: number | null;
  uptime_seconds: number | null;
  uptime_formatted: string | null;
}

interface BackupRun {
  started_at: string;
  completed_at: string | null;
  status: string;
  trigger: string;
  size_bytes: number | null;
  duration_s: number | null;
  error: string | null;
}

interface BackupTargetStatus {
  target: string;
  monitor_instance: string | null;
  state: 'success' | 'failed' | 'running' | 'overdue' | 'none';
  last_run: BackupRun | null;
  last_success: BackupRun | null;
  local_count: number;
  local_bytes: number;
}

interface BackupStatus {
  targets: BackupTargetStatus[];
  schedule: string;
  local_keep: number;
  remote_keep: number;
  disk: { total: number; used: number; free: number };
}

interface ServerHealth {
  cpu: {
    percent: number;
    count: number;
    load_1m: number;
    load_5m: number;
    load_15m: number;
  };
  memory: {
    total_gb: number;
    used_gb: number;
    percent: number;
  };
  disk: {
    total_gb: number;
    used_gb: number;
    percent: number;
  };
  uptime: {
    days: number;
    hours: number;
    formatted: string;
  };
  status: string;
}

interface MonitoringStatus {
  is_running: boolean;
  instances: { [key: string]: InstanceStatus };
  config: {
    email_enabled: boolean;
    email_recipient: string;
    smtp_configured: boolean;
    check_interval_minutes: number;
  };
  timestamp: string;
}

interface MonitoringAlert {
  id: number;
  instance_name: string;
  severity: string;
  alert_type: string;
  message: string;
  log_excerpt: string;
  details: any;
  acknowledged: boolean;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  created_at: string;
}

interface FedTradeHealth {
  server: string;
  path: string;
  checked_at: string;
  reachable: boolean;
  running: boolean | null;
  processes?: { pid: number; uptime_s: number }[];
  service?: { unit: string; active_state: string; sub_state: string; main_pid: number | null; n_restarts: number | null; since: string | null } | null;
  log?: { path: string; size: number; last_write: string };
  db?: {
    docs_by_status?: Record<string, number>;
    last_first_seen_at?: string | null;
    last_audit?: { ts: string; event: string } | null;
    snapshots_by_status?: Record<string, number>;
    error?: string;
  };
  error?: string;
}

interface EmailConfig {
  email_enabled: boolean;
  email_recipient: string;
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_password: string;
  smtp_use_tls: boolean;
}

const severityColors: { [key: string]: string } = {
  CRITICAL: '#f44336',
  HIGH: '#ff9800',
  MEDIUM: '#ffc107',
  LOW: '#4caf50',
};

const severityEmojis: { [key: string]: string } = {
  CRITICAL: '🔴',
  HIGH: '🟠',
  MEDIUM: '🟡',
  LOW: '🟢',
};

// Map internal instance names to display names (matches Dashboard)
const instanceDisplayNames: { [key: string]: string } = {
  'V1': 'OPTIONS',
  'E_OPTIONS': 'E-OPTIONS',
  'TOPORB': 'TIER2',
  'OPTIONS_PCS': 'OPTIONS PCS',
  'FUTURES': 'Futures',
  'FEDTRADE': 'FedTrade',
};

const getDisplayName = (instanceName: string): string => {
  return instanceDisplayNames[instanceName] || instanceName;
};

const Monitoring: React.FC = () => {
  const { token } = useAuth();
  const [status, setStatus] = useState<MonitoringStatus | null>(null);
  const [alerts, setAlerts] = useState<MonitoringAlert[]>([]);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Filters
  const [filterSeverity, setFilterSeverity] = useState<string>('');
  const [filterInstance, setFilterInstance] = useState<string>('');
  const [filterAcknowledged, setFilterAcknowledged] = useState<string>('');
  
  // Expanded rows
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  
  // Config dialog
  const [configOpen, setConfigOpen] = useState(false);
  const [emailConfig, setEmailConfig] = useState<EmailConfig>({
    email_enabled: true,
    email_recipient: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  
  // Manual run
  const [runningCheck, setRunningCheck] = useState(false);
  
  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Server health
  const [serverHealth, setServerHealth] = useState<ServerHealth | null>(null);

  // Daily deployment backups (one target per instance card + DASHBOARD)
  const [backups, setBackups] = useState<BackupStatus | null>(null);

  // FedTrade scanner (KnightRider) — fedtrade.service + log + SQLite state
  const [fedHealth, setFedHealth] = useState<FedTradeHealth | null>(null);

  const getAuthHeaders = useCallback(() => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, [token]);

  const fetchServerHealth = useCallback(async () => {
    try {
      const response = await fetch('/api/monitoring/server-health', {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch server health');
      const data = await response.json();
      setServerHealth(data);
    } catch (err) {
      console.error('Error fetching server health:', err);
    }
  }, [getAuthHeaders]);

  const fetchBackups = useCallback(async () => {
    try {
      const response = await fetch('/api/backups/status', { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch backup status');
      setBackups(await response.json());
    } catch (err) {
      console.error('Error fetching backup status:', err);
    }
    try {
      const response = await fetch('/api/fedtrade/health', { headers: getAuthHeaders() });
      if (response.ok) setFedHealth(await response.json());
    } catch (err) {
      console.error('Error fetching FedTrade health:', err);
    }
  }, [getAuthHeaders]);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/monitoring/status', {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch status');
      const data = await response.json();
      setStatus(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch status');
    }
  }, [getAuthHeaders]);

  const fetchAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', rowsPerPage.toString());
      params.append('offset', (page * rowsPerPage).toString());
      if (filterSeverity) params.append('severity', filterSeverity);
      if (filterInstance) params.append('instance_name', filterInstance);
      if (filterAcknowledged === 'true') params.append('acknowledged', 'true');
      if (filterAcknowledged === 'false') params.append('acknowledged', 'false');
      
      const response = await fetch(`/api/monitoring/alerts?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch alerts');
      const data = await response.json();
      setAlerts(data.alerts);
      setTotalAlerts(data.total);
    } catch (err) {
      console.error('Error fetching alerts:', err);
    } finally {
      setAlertsLoading(false);
    }
  }, [getAuthHeaders, page, rowsPerPage, filterSeverity, filterInstance, filterAcknowledged]);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await fetch('/api/monitoring/config', {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch config');
      const data = await response.json();
      setEmailConfig({
        ...emailConfig,
        ...data,
        smtp_password: '', // Don't populate password field
      });
    } catch (err) {
      console.error('Error fetching config:', err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStatus(), fetchAlerts(), fetchServerHealth(), fetchBackups()]);
      setLoading(false);
    };
    loadData();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchStatus();
      fetchAlerts();
      fetchServerHealth();
      fetchBackups();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchStatus, fetchAlerts, fetchServerHealth, fetchBackups]);

  useEffect(() => {
    fetchAlerts();
  }, [page, rowsPerPage, filterSeverity, filterInstance, filterAcknowledged, fetchAlerts]);

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([fetchStatus(), fetchAlerts(), fetchServerHealth(), fetchBackups()]);
    setLoading(false);
  };

  const handleAcknowledge = async (alertId: number) => {
    try {
      const response = await fetch(`/api/monitoring/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to acknowledge');
      fetchAlerts();
    } catch (err) {
      console.error('Error acknowledging alert:', err);
    }
  };

  const handleDeleteSingleAlert = async (alertId: number) => {
    try {
      const response = await fetch(`/api/monitoring/alerts/${alertId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to delete');
      fetchAlerts();
      fetchStatus();
    } catch (err) {
      console.error('Error deleting alert:', err);
    }
  };

  const handleToggleInstance = async (instanceName: string, enabled: boolean) => {
    try {
      const response = await fetch(`/api/monitoring/instance/${instanceName}/toggle`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ enabled }),
      });
      if (!response.ok) throw new Error('Failed to toggle');
      fetchStatus();
    } catch (err) {
      console.error('Error toggling instance:', err);
    }
  };

  const handleOpenConfig = async () => {
    await fetchConfig();
    setConfigOpen(true);
  };

  const handleSaveConfig = async () => {
    setConfigSaving(true);
    try {
      const response = await fetch('/api/monitoring/config', {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(emailConfig),
      });
      if (!response.ok) throw new Error('Failed to save config');
      setConfigOpen(false);
      fetchStatus();
    } catch (err) {
      console.error('Error saving config:', err);
    } finally {
      setConfigSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      // First save config if password provided
      if (emailConfig.smtp_password) {
        await fetch('/api/monitoring/config', {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(emailConfig),
        });
      }
      
      const response = await fetch('/api/monitoring/test-email', {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        alert('Test email sent successfully!');
      } else {
        alert(`Failed to send test email: ${data.message}`);
      }
    } catch (err) {
      alert('Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleRunNow = async () => {
    setRunningCheck(true);
    try {
      const response = await fetch('/api/monitoring/run-now', {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to run check');
      const data = await response.json();
      alert(`Monitoring check complete: ${data.total_alerts || 0} alerts detected`);
      fetchStatus();
      fetchAlerts();
    } catch (err) {
      console.error('Error running check:', err);
    } finally {
      setRunningCheck(false);
    }
  };

  const handleDeleteAllAlerts = async () => {
    setDeleting(true);
    try {
      const response = await fetch('/api/monitoring/alerts', {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to delete alerts');
      const data = await response.json();
      alert(`Deleted ${data.deleted_count} alerts`);
      setDeleteConfirmOpen(false);
      fetchStatus();
      fetchAlerts();
    } catch (err) {
      console.error('Error deleting alerts:', err);
      alert('Failed to delete alerts');
    } finally {
      setDeleting(false);
    }
  };

  const toggleRowExpand = (id: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const formatTimestamp = (ts: string) => {
    try {
      const date = new Date(ts);
      // Always display in Eastern Time to match market hours
      return date.toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      }) + ' ET';
    } catch {
      return ts;
    }
  };

  const getHealthIcon = (isHealthy: boolean, isEnabled: boolean) => {
    if (!isEnabled) {
      return <VisibilityOffIcon sx={{ color: '#999' }} />;
    }
    return isHealthy ? (
      <CheckCircleIcon sx={{ color: '#4caf50' }} />
    ) : (
      <ErrorIcon sx={{ color: '#f44336' }} />
    );
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return <ErrorIcon sx={{ color: severityColors.CRITICAL }} />;
      case 'HIGH':
        return <WarningIcon sx={{ color: severityColors.HIGH }} />;
      case 'MEDIUM':
        return <WarningIcon sx={{ color: severityColors.MEDIUM }} />;
      case 'LOW':
        return <InfoIcon sx={{ color: severityColors.LOW }} />;
      default:
        return <InfoIcon />;
    }
  };

  const backupFor = (instanceName: string | null) =>
    backups?.targets.find((t) => (instanceName === null ? t.target === 'DASHBOARD' : t.monitor_instance === instanceName));

  const renderBackupLine = (t: BackupTargetStatus | undefined) => {
    if (!backups) return null;
    const shortTs = (ts: string) =>
      new Date(ts).toLocaleString('en-US', {
        timeZone: 'America/New_York', weekday: 'short', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    const mb = (b: number | null | undefined) => (b != null ? `${(b / 1e6).toFixed(0)} MB` : '?');
    if (!t) {
      return (
        <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255, 255, 255, 0.4)' }}>
          Backup: no backup script configured
        </Typography>
      );
    }
    const ok = t.last_success;
    const last = t.last_run;
    const look: Record<BackupTargetStatus['state'], { color: string; text: string }> = {
      success: { color: '#4caf50', text: ok ? `✓ ${shortTs(ok.started_at)} · ${mb(ok.size_bytes)} · ${t.local_count} kept` : '✓' },
      failed: { color: '#f44336', text: `✗ FAILED ${last ? shortTs(last.started_at) : ''}${ok ? ` (last good ${shortTs(ok.started_at)})` : ''}` },
      running: { color: '#29b6f6', text: `⟳ running since ${last ? shortTs(last.started_at) : '?'}` },
      overdue: { color: '#ff9800', text: `⚠ OVERDUE — last good ${ok ? shortTs(ok.started_at) : 'never'}` },
      none: { color: 'rgba(255, 255, 255, 0.5)', text: `no backup yet (first run ${backups.schedule})` },
    };
    const { color, text } = look[t.state];
    const tip = [
      `Target ${t.target} · ${backups.schedule} · keep ${backups.local_keep} here, ${backups.remote_keep} on the server`,
      last ? `Last run: ${last.status} (${last.trigger}) ${formatTimestamp(last.started_at)}${last.duration_s != null ? `, ${Math.round(last.duration_s)} s` : ''}` : 'Never run',
      last?.error ? `Error: ${last.error}` : '',
      `Stored here: ${t.local_count} cop${t.local_count === 1 ? 'y' : 'ies'}, ${(t.local_bytes / 1e9).toFixed(1)} GB`,
    ].filter(Boolean).join('\n');
    return (
      <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>} arrow>
        <Typography variant="caption" sx={{ display: 'block', color, cursor: 'help', fontWeight: t.state === 'success' || t.state === 'none' ? 400 : 600 }}>
          Backup: {text}
        </Typography>
      </Tooltip>
    );
  };

  if (loading && !status) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const totalCritical = status ? Object.values(status.instances).reduce((sum, i) => sum + i.alerts_by_severity.CRITICAL, 0) : 0;
  const totalHigh = status ? Object.values(status.instances).reduce((sum, i) => sum + i.alerts_by_severity.HIGH, 0) : 0;
  const healthyCount = status ? Object.values(status.instances).filter(i => i.is_healthy && i.is_enabled).length : 0;
  const totalInstances = status ? Object.keys(status.instances).length : 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#fff' }}>
            🖥️ System Monitoring
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }}>
            Real-time log monitoring and alerts for all trading instances
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={runningCheck ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            onClick={handleRunNow}
            disabled={runningCheck}
          >
            Run Check Now
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={() => setDeleteConfirmOpen(true)}
          >
            Clear Alerts
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={handleOpenConfig}
          >
            Configure
          </Button>
          <IconButton onClick={handleRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Summary Cards - 5 cards in one row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', md: '1 1 0' }, minWidth: { xs: 'calc(50% - 8px)', md: 0 } }}>
          <Card sx={{ bgcolor: '#2d2d2d', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Healthy Instances
              </Typography>
              <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                {healthyCount} / {totalInstances}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', md: '1 1 0' }, minWidth: { xs: 'calc(50% - 8px)', md: 0 } }}>
          <Card sx={{ bgcolor: '#2d2d2d', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Critical Alerts (48h)
              </Typography>
              <Typography variant="h4" sx={{ color: totalCritical > 0 ? '#f44336' : '#4caf50', fontWeight: 'bold' }}>
                {totalCritical}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', md: '1 1 0' }, minWidth: { xs: 'calc(50% - 8px)', md: 0 } }}>
          <Card sx={{ bgcolor: '#2d2d2d', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                High Alerts (48h)
              </Typography>
              <Typography variant="h4" sx={{ color: totalHigh > 0 ? '#ff9800' : '#4caf50', fontWeight: 'bold' }}>
                {totalHigh}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', md: '1 1 0' }, minWidth: { xs: 'calc(50% - 8px)', md: 0 } }}>
          <Card sx={{ bgcolor: '#2d2d2d', height: '100%', border: serverHealth?.status === 'warning' ? '1px solid #ff9800' : undefined }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Dashboard Server
              </Typography>
              {serverHealth ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="h5" sx={{ color: serverHealth.status === 'healthy' ? '#4caf50' : '#ff9800', fontWeight: 'bold' }}>
                      {serverHealth.cpu.load_1m}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      load
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      CPU: {serverHealth.cpu.percent}%
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      RAM: {serverHealth.memory.percent}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }}>
                    Uptime: {serverHealth.uptime.formatted}
                  </Typography>
                  {renderBackupLine(backupFor(null))}
                  {backups && (
                    <Typography variant="caption" sx={{ display: 'block', color: backups.disk.free < 15e9 ? '#ff9800' : 'rgba(255, 255, 255, 0.5)' }}>
                      Disk free: {(backups.disk.free / 1e9).toFixed(0)} GB
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  Loading...
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', md: '1 1 0' }, minWidth: { xs: 'calc(50% - 8px)', md: 0 } }}>
          <Card sx={{ bgcolor: '#2d2d2d', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Email Alerts
              </Typography>
              <Typography variant="h4" sx={{ color: status?.config.email_enabled ? '#4caf50' : '#999', fontWeight: 'bold' }}>
                {status?.config.email_enabled ? 'ON' : 'OFF'}
              </Typography>
              {status?.config.smtp_configured && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {status.config.email_recipient}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Instance Health Cards */}
      <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
        Instance Health
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {status && Object.entries(status.instances).map(([name, instance]) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={name}>
            <Card 
              sx={{ 
                bgcolor: '#2d2d2d',
                border: `1px solid ${instance.is_healthy ? '#333' : '#f44336'}`,
                opacity: instance.is_enabled ? 1 : 0.6,
              }}
            >
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getHealthIcon(instance.is_healthy, instance.is_enabled)}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#fff' }}>
                      {getDisplayName(name)}
                    </Typography>
                  </Box>
                  <Switch
                    size="small"
                    checked={instance.is_enabled}
                    onChange={(e) => handleToggleInstance(name, e.target.checked)}
                  />
                </Box>
                
                {/* Server + folder path, so instances aren't confused across hosts */}
                {(instance.server || instance.path) && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 1,
                      color: 'rgba(255, 255, 255, 0.55)',
                      fontFamily: 'monospace',
                      fontSize: '0.68rem',
                      wordBreak: 'break-all',
                    }}
                  >
                    {instance.server || '?'} · {instance.path || '?'}
                  </Typography>
                )}
                
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                  {instance.alerts_by_severity.CRITICAL > 0 && (
                    <Chip 
                      size="small" 
                      label={`🔴 ${instance.alerts_by_severity.CRITICAL}`}
                      sx={{ bgcolor: 'rgba(244, 67, 54, 0.2)', color: '#f44336' }}
                    />
                  )}
                  {instance.alerts_by_severity.HIGH > 0 && (
                    <Chip 
                      size="small" 
                      label={`🟠 ${instance.alerts_by_severity.HIGH}`}
                      sx={{ bgcolor: 'rgba(255, 152, 0, 0.2)', color: '#ff9800' }}
                    />
                  )}
                  {instance.alerts_by_severity.MEDIUM > 0 && (
                    <Chip 
                      size="small" 
                      label={`🟡 ${instance.alerts_by_severity.MEDIUM}`}
                      sx={{ bgcolor: 'rgba(255, 193, 7, 0.2)', color: '#ffc107' }}
                    />
                  )}
                  {instance.alert_count_24h === 0 && (
                    <Chip 
                      size="small" 
                      label="✓ No alerts"
                      sx={{ bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#4caf50' }}
                    />
                  )}
                </Box>
                
                {/* CPU / Memory / server uptime metrics */}
                {(instance.cpu_load !== null || instance.memory_percent !== null || instance.uptime_formatted !== null) && (
                  <Box sx={{ display: 'flex', gap: 2, mb: 0.5, flexWrap: 'wrap' }}>
                    {instance.cpu_load !== null && (
                      <Tooltip title="1-minute load average on the strategy server" arrow>
                        <Typography variant="caption" sx={{ color: instance.cpu_load > 2 ? '#ff9800' : 'rgba(255, 255, 255, 0.7)', cursor: 'help' }}>
                          CPU: {instance.cpu_load.toFixed(2)}
                        </Typography>
                      </Tooltip>
                    )}
                    {instance.memory_percent !== null && (
                      <Tooltip
                        title={
                          instance.memory_used_mb !== null && instance.memory_total_mb !== null
                            ? `${(instance.memory_used_mb / 1024).toFixed(1)} GB used of ${(instance.memory_total_mb / 1024).toFixed(1)} GB`
                            : 'Memory usage on the strategy server'
                        }
                        arrow
                      >
                        <Typography variant="caption" sx={{ color: instance.memory_percent > 80 ? '#ff9800' : 'rgba(255, 255, 255, 0.7)', cursor: 'help' }}>
                          MEM: {instance.memory_percent}%
                        </Typography>
                      </Tooltip>
                    )}
                    {instance.uptime_formatted !== null && (
                      <Tooltip title="Server uptime (time since last reboot)" arrow>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', cursor: 'help' }}>
                          Up: {instance.uptime_formatted}
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                )}
                
                <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
                  Last check: {instance.last_check ? formatTimestamp(instance.last_check) : 'Never'}
                </Typography>
                {renderBackupLine(backupFor(name))}
              </CardContent>
            </Card>
          </Grid>
        ))}
        {fedHealth && (() => {
          const up = fedHealth.running === true;
          const proc = fedHealth.processes?.[0];
          const docs = fedHealth.db?.docs_by_status || {};
          const fedAlerts = alerts.filter((a) => a.instance_name === 'FEDTRADE' && !a.acknowledged);
          const upFor = proc ? `${Math.floor(proc.uptime_s / 86400)}d ${Math.floor((proc.uptime_s % 86400) / 3600)}h ${Math.floor((proc.uptime_s % 3600) / 60)}m` : null;
          return (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card sx={{ bgcolor: '#2d2d2d', border: `1px solid ${up ? '#333' : '#f44336'}` }}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {getHealthIcon(up, true)}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#fff' }}>FedTrade</Typography>
                    <Chip size="small" label="scanner · no orders" sx={{ height: 18, fontSize: '0.65rem' }} />
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'rgba(255, 255, 255, 0.55)', fontFamily: 'monospace', fontSize: '0.68rem' }}>
                    {fedHealth.server} · {fedHealth.path}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                    {fedAlerts.length > 0 ? (
                      <Chip size="small" label={`${fedAlerts.some((a) => a.severity === 'CRITICAL') ? '🔴' : '🟠'} ${fedAlerts.length}`} sx={{ bgcolor: 'rgba(244, 67, 54, 0.2)', color: '#f44336' }} />
                    ) : (
                      <Chip size="small" label="✓ No alerts" sx={{ bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#4caf50' }} />
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', color: up ? 'rgba(255, 255, 255, 0.7)' : '#f44336' }}>
                    {fedHealth.reachable === false
                      ? 'Server unreachable'
                      : fedHealth.service
                        ? `${fedHealth.service.unit}: ${fedHealth.service.active_state} (${fedHealth.service.sub_state})${up && upFor ? ` · up ${upFor}` : ''}`
                        : up ? `Process: running (pid ${proc?.pid}, up ${upFor})` : 'Process: NOT RUNNING'}
                  </Typography>
                  {fedHealth.service?.n_restarts != null && (
                    <Typography variant="caption" sx={{ display: 'block', color: fedHealth.service.n_restarts > 0 ? '#ff9800' : 'rgba(255, 255, 255, 0.7)' }}>
                      Auto-restarts: {fedHealth.service.n_restarts}
                    </Typography>
                  )}
                  {fedHealth.log && (
                    <Tooltip title={`${fedHealth.log.path} · ${(fedHealth.log.size / 1024).toFixed(1)} KB · quiet outside the 8:44:45 AM–5:45 PM ET poll window`} arrow>
                      <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', cursor: 'help' }}>
                        Log last written: {formatTimestamp(fedHealth.log.last_write)}
                      </Typography>
                    </Tooltip>
                  )}
                  {Object.keys(docs).length > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
                      Docs: {Object.entries(docs).map(([k, n]) => `${n} ${k}`).join(' · ')}
                    </Typography>
                  )}
                  {fedHealth.db?.last_audit && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
                      Last event: {fedHealth.db.last_audit.event} · {formatTimestamp(fedHealth.db.last_audit.ts)}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
                    Last check: {formatTimestamp(fedHealth.checked_at)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255, 255, 255, 0.5)' }}>
                    Backup: no backup script configured
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })()}
      </Grid>

      {/* Alert Feed */}
      <Paper sx={{ bgcolor: '#2d2d2d', p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: '#fff' }}>
            Alert History
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Severity</InputLabel>
              <Select
                value={filterSeverity}
                label="Severity"
                onChange={(e) => { setFilterSeverity(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CRITICAL">🔴 Critical</MenuItem>
                <MenuItem value="HIGH">🟠 High</MenuItem>
                <MenuItem value="MEDIUM">🟡 Medium</MenuItem>
                <MenuItem value="LOW">🟢 Low</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Instance</InputLabel>
              <Select
                value={filterInstance}
                label="Instance"
                onChange={(e) => { setFilterInstance(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                {status && Object.keys(status.instances).map(name => (
                  <MenuItem key={name} value={name}>{getDisplayName(name)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterAcknowledged}
                label="Status"
                onChange={(e) => { setFilterAcknowledged(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="false">Unacknowledged</MenuItem>
                <MenuItem value="true">Acknowledged</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }} width={40}></TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Time</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Severity</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Instance</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Type</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Message</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Status</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }} width={100}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alertsLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : alerts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ color: 'rgba(255, 255, 255, 0.7)', py: 4 }}>
                    No alerts found
                  </TableCell>
                </TableRow>
              ) : (
                alerts.map((alert) => (
                  <React.Fragment key={alert.id}>
                    <TableRow 
                      sx={{ 
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                        opacity: alert.acknowledged ? 0.6 : 1,
                      }}
                    >
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleRowExpand(alert.id)}>
                          {expandedRows.has(alert.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ color: '#ccc', whiteSpace: 'nowrap' }}>
                        {formatTimestamp(alert.created_at)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={getSeverityIcon(alert.severity)}
                          label={alert.severity}
                          sx={{ 
                            bgcolor: `${severityColors[alert.severity]}20`,
                            color: severityColors[alert.severity],
                            '& .MuiChip-icon': { color: 'inherit' }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                        {getDisplayName(alert.instance_name)}
                      </TableCell>
                      <TableCell sx={{ color: '#ccc' }}>
                        {alert.alert_type.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell sx={{ color: '#ccc', maxWidth: 300 }}>
                        <Typography variant="body2" noWrap title={alert.message}>
                          {alert.message}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {alert.acknowledged ? (
                          <Tooltip title={`Acknowledged by ${alert.acknowledged_by} at ${formatTimestamp(alert.acknowledged_at || '')}`}>
                            <Chip size="small" label="Ack" color="success" variant="outlined" />
                          </Tooltip>
                        ) : (
                          <Chip size="small" label="New" color="warning" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {!alert.acknowledged && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleAcknowledge(alert.id)}
                            >
                              Ack
                            </Button>
                          )}
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteSingleAlert(alert.id)}
                            title="Delete alert"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, borderBottom: expandedRows.has(alert.id) ? undefined : 'none' }}>
                        <Collapse in={expandedRows.has(alert.id)} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 2, bgcolor: '#1a1a1a', borderRadius: 1, my: 1 }}>
                            <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                              Log Excerpt:
                            </Typography>
                            <Box 
                              component="pre" 
                              sx={{ 
                                bgcolor: '#0d0d0d', 
                                p: 2, 
                                borderRadius: 1, 
                                overflow: 'auto',
                                fontSize: '12px',
                                color: '#ddd',
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                                maxHeight: 200,
                              }}
                            >
                              {alert.log_excerpt || 'No log excerpt available'}
                            </Box>
                            {alert.details && Object.keys(alert.details).length > 0 && (
                              <>
                                <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 2, mb: 1 }}>
                                  Details:
                                </Typography>
                                <Box sx={{ color: '#ccc' }}>
                                  {Object.entries(alert.details).map(([key, value]) => (
                                    <Typography key={key} variant="body2">
                                      <strong>{key}:</strong> {String(value)}
                                    </Typography>
                                  ))}
                                </Box>
                              </>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalAlerts}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Clear All Alerts?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete all monitoring alerts? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAllAlerts} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteSweepIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Configuration Dialog */}
      <Dialog open={configOpen} onClose={() => setConfigOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Monitoring Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={emailConfig.email_enabled}
                  onChange={(e) => setEmailConfig({ ...emailConfig, email_enabled: e.target.checked })}
                />
              }
              label="Enable Email Alerts"
            />
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
              Email Settings
            </Typography>
            
            <TextField
              fullWidth
              label="Email Recipient"
              value={emailConfig.email_recipient}
              onChange={(e) => setEmailConfig({ ...emailConfig, email_recipient: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="alerts@example.com"
            />
            
            <TextField
              fullWidth
              label="SMTP Host"
              value={emailConfig.smtp_host}
              onChange={(e) => setEmailConfig({ ...emailConfig, smtp_host: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="mail.example.com"
            />
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="SMTP Port"
                  type="number"
                  value={emailConfig.smtp_port}
                  onChange={(e) => setEmailConfig({ ...emailConfig, smtp_port: parseInt(e.target.value) || 587 })}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailConfig.smtp_use_tls}
                      onChange={(e) => setEmailConfig({ ...emailConfig, smtp_use_tls: e.target.checked })}
                    />
                  }
                  label="Use TLS"
                  sx={{ mt: 1 }}
                />
              </Grid>
            </Grid>
            
            <TextField
              fullWidth
              label="SMTP Username"
              value={emailConfig.smtp_username}
              onChange={(e) => setEmailConfig({ ...emailConfig, smtp_username: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="user@example.com"
            />
            
            <TextField
              fullWidth
              label="SMTP Password"
              type={showPassword ? 'text' : 'password'}
              value={emailConfig.smtp_password}
              onChange={(e) => setEmailConfig({ ...emailConfig, smtp_password: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="Enter password to update"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              variant="outlined"
              startIcon={testingEmail ? <CircularProgress size={16} /> : <EmailIcon />}
              onClick={handleTestEmail}
              disabled={testingEmail || !emailConfig.email_recipient}
            >
              Send Test Email
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveConfig} 
            variant="contained"
            disabled={configSaving}
          >
            {configSaving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Monitoring;

